/**
 * useWhatsAppViewMute — read+write per-view mute state for WhatsApp alerts.
 *
 * Backend storage: users/{uid}.whatsapp.disabledViewIds (string[])
 * A view in the list will not receive WhatsApp notifications, regardless
 * of which event keys are enabled in whatsapp.enabledEvents.
 */

import { useEffect, useState, useCallback } from 'react';
import { doc, onSnapshot, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuthStore } from '@/store/authStore';

export function useWhatsAppViewMute() {
  const { user } = useAuthStore();
  const [disabledViewIds, setDisabledViewIds] = useState<Set<string>>(new Set());
  const [isConfigured, setIsConfigured] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setDisabledViewIds(new Set());
      setIsConfigured(false);
      return;
    }
    const unsub = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      const wa = snap.data()?.whatsapp ?? {};
      const list = (wa.disabledViewIds ?? []) as string[];
      setDisabledViewIds(new Set(list));
      setIsConfigured(wa.verified === true);
    });
    return () => unsub();
  }, [user?.uid]);

  const isMuted = useCallback(
    (viewId: string) => disabledViewIds.has(viewId),
    [disabledViewIds],
  );

  const setMuted = useCallback(
    async (viewId: string, muted: boolean) => {
      if (!user?.uid) return;
      await updateDoc(doc(db, 'users', user.uid), {
        'whatsapp.disabledViewIds': muted ? arrayUnion(viewId) : arrayRemove(viewId),
      });
    },
    [user?.uid],
  );

  return { isMuted, setMuted, isConfigured };
}
